import React, { forwardRef, useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import api from '../../services/api';
import { AppResponse } from '../../models/common/app-response';

export interface TenantShortDto {
    id: number;
    name: string;
}

interface TenantAutocompleteProps {
    tenant: TenantShortDto | null;
    tenantId?: number | null;
    onTenantChange: (newTenant: TenantShortDto | null) => void;
    label?: string;
    style?: React.CSSProperties;
}

const TenantAutocomplete = forwardRef<HTMLDivElement, TenantAutocompleteProps>(({ tenant, tenantId, onTenantChange, label = "Медорганизация", style = {} }, ref) => {
    const [tenants, setTenants] = useState<TenantShortDto[]>([]);
    const [initialTenants, setInitialTenants] = useState<TenantShortDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        api.get<AppResponse<TenantShortDto[]>>('/api/v1/tenants/search')
            .then(response => {
                setTenants(response.data.data);
                setInitialTenants(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching tenants:', error);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (tenant == null && tenantId != null && initialTenants.length > 0) {
            const selectedTenant = initialTenants.find(item => item.id === tenantId);
            if (selectedTenant) {
                onTenantChange(selectedTenant);
            }
        }
    }, [initialTenants, onTenantChange, tenant, tenantId]);

    const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
        if (newInputValue.length >= 3) {
            const filtered = initialTenants.filter(t =>
                t.name.toLowerCase().includes(newInputValue.toLowerCase())
            );
            setTenants(filtered);
        } else {
            setTenants(initialTenants);
        }
    };

    const handleChange = (_: React.SyntheticEvent, newValue: TenantShortDto | null) => {
        onTenantChange(newValue);
    };

    return (
        <Autocomplete
            options={tenants}
            getOptionLabel={(option) => option.name}
            value={tenant}
            onChange={handleChange}
            onInputChange={handleInputChange}
            noOptionsText="Не найдено"
            isOptionEqualToValue={(option, value) => value == null ? false : option.id === value.id}
            loading={loading}
            loadingText="Поиск..."
            ref={ref}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={style}
                    placeholder="Введите от 3х букв"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
});

export default TenantAutocomplete;
